/* eslint-disable no-nested-ternary */
import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useMediaQuery, Typography } from '@mui/material';
import { useTheme } from '@mui/styles';
import ProcessHandler from '@src/components/ProcessHandler';
import { PACKAGE_TYPE } from '@src/constants/package';
import api from '@src/apis';
import smallLogoTts from '@src/assets/logo/small-logo.svg';

import smallLogoDubbing from '@src/assets/logo/small-logo-dubbing.svg';
import {
  checkHasDiscountPackage,
  getMostPopularPackage,
  checkPackageDiscount,
} from '@src/services/package';
import { LANGUAGE } from '@src/constants';
import useElementWidth from '@src/hooks/useElementWidth';
import { FEATURE_KEYS } from '@src/configs/featureKeys';
import useFeatureFlags from '@src/hooks/useFeatureFlags';
import { useCheckDubbingRoute } from '@src/hooks/useCheckDubbingRoute';
import moment from 'moment';
import { StyledPackages } from './index.style';
import PackageTypes from '../PackageTypes';

import NewPackageTypes from '../MobilePackageTypes';
import PackageItemSMS from './PackageItemSMS';

const PACKAGE_RESPONSIVE_BREAKPOINT = 1167;
const PACKAGE_RESPONSIVE_MOBILE_BREAKPOINT = 600;

const SMSPackages = () => {
  const theme = useTheme();
  const isDubbingRoute = useCheckDubbingRoute();
  const localtion = useLocation();
  const history = useHistory();
  const queryParams = new URLSearchParams(localtion.search);
  const getPackageType = () => {
    const packageTypeParam =
      queryParams.get('packageType') || PACKAGE_TYPE.MBF_STUDIO;
    return Object.values(PACKAGE_TYPE).includes(packageTypeParam)
      ? packageTypeParam
      : PACKAGE_TYPE.MBF_STUDIO;
  };

  const initialPackageType = isDubbingRoute
    ? PACKAGE_TYPE.MBF_DUBBING
    : getPackageType();

  const [chosenPackage, setChosenPackage] = useState(null);
  const [packageType, setPackageType] = useState(initialPackageType);

  const [packages, setPackages] = useState([]);

  const [currentPackages, setCurrentPackages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasDiscountPackage, setHasDiscountPackage] = useState(false);
  const [isFirstTimePaid, setIsFirstTimePaid] = useState(false);

  const { t, i18n } = useTranslation();
  const { language } = i18n;

  const packagesRef = useRef();
  const packagesWidth = useElementWidth(packagesRef);

  const { user } = useSelector((state) => state.auth);

  const { getFeatureValue } = useFeatureFlags();

  const discountForFirstPaidUser = getFeatureValue(
    FEATURE_KEYS.DISCOUNT_FOR_FIRST_PAID_USER,
  );

  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const useNewPackageMobile = isMobile;

  const handleChangePackageType = (type) => {
    setPackageType(type);
    queryParams.set('packageType', type);
    history.replace({ search: queryParams.toString() });
  };

  const getHighlightPackageCode = (pkgs) => {
    const popularPackage = getMostPopularPackage(pkgs);
    return popularPackage?.code;
  };

  const packageTypeTabs = [
    {
      label: 'aivoiceStudio',
      id: PACKAGE_TYPE.MBF_STUDIO,
    },
    { label: 'PKG_DUBBING', id: PACKAGE_TYPE.MBF_DUBBING },
  ];

  const newPackageTypeTabs = [
    {
      label: 'aivoiceStudio',
      id: PACKAGE_TYPE.MBF_STUDIO,
      logo: smallLogoTts,
    },
    {
      label: 'PKG_DUBBING',
      id: PACKAGE_TYPE.MBF_DUBBING,
      logo: smallLogoDubbing,
    },
  ];

  const handleSetCurrentPackages = (allPackages) => {
    setCurrentPackages(allPackages?.sort((a, b) => a.rank - b.rank));
    // setSaveUpToPercent(allPackages[1]?.yearDiscount || null);
    const isHasDiscountPackage = checkHasDiscountPackage(allPackages);
    setHasDiscountPackage(isHasDiscountPackage);
  };

  const handleDiscountEnd = () => {
    const isHasDiscountPackage = checkHasDiscountPackage(currentPackages);
    setHasDiscountPackage(isHasDiscountPackage);
  };

  const fetchPackages = async () => {
    setLoading(true);
    const { status, result } = await api.packages.getPackages({
      type: packageType,
      active: true,
    });
    setLoading(false);
    if (status) setPackages(result.packages);
  };

  // Show package highlight in the first row when responsive
  const packageSort = (pkg1, pkg2) => {
    const { code: code1, discountConfig: discountConfig1 } = pkg1;
    const { code: code2, discountConfig: discountConfig2 } = pkg2;
    const highlightPackage = getHighlightPackageCode(currentPackages);

    if (code1 === highlightPackage) return -1;
    if (code2 === highlightPackage) return 1;

    if (
      checkPackageDiscount(discountConfig1) &&
      !checkPackageDiscount(discountConfig2)
    )
      return -1;
    if (
      !checkPackageDiscount(discountConfig1) &&
      checkPackageDiscount(discountConfig2)
    )
      return 1;

    return 0;
  };

  const packageSortMobile = (pkg1, pkg2) => {
    const { code: code1, discountConfig: discountConfig1 } = pkg1;
    const { code: code2, discountConfig: discountConfig2 } = pkg2;
    const highlightPackage = getHighlightPackageCode(currentPackages);

    if (code1 === highlightPackage) return -1;
    if (code2 === highlightPackage) return 1;

    if (
      checkPackageDiscount(discountConfig1) &&
      !checkPackageDiscount(discountConfig2)
    )
      return 1;
    if (
      !checkPackageDiscount(discountConfig1) &&
      checkPackageDiscount(discountConfig2)
    )
      return -1;

    return 0;
  };

  const checkExpiredDate = (expiryDate) => {
    if (!expiryDate) return false;
    return moment().isAfter(expiryDate);
  };

  const getUsingPackage = (pkgType) => {
    switch (pkgType) {
      case PACKAGE_TYPE.MBF_STUDIO: {
        const studioPackage = packages.find(
          (pkg) => pkg.code === user?.packageCode,
        );
        return {
          ...studioPackage,
          isExpires: checkExpiredDate(user?.packageExpiryDate),
        };
      }

      case PACKAGE_TYPE.MBF_DUBBING: {
        const dubbingPackage = packages.find(
          (pkg) => pkg.code === user?.dubbing?.packageCode,
        );
        return {
          ...dubbingPackage,
          isExpires: checkExpiredDate(user?.dubbing?.packageExpiryDate),
        };
      }
      default:
        return null;
    }
  };

  const handleChoosePackageMobile = (pkg) => {
    if (!useNewPackageMobile) return;
    if (chosenPackage && chosenPackage.id === pkg.id) {
      setChosenPackage(null);
    } else {
      setChosenPackage(pkg);
    }
  };

  const renderFlagUI = (item) => (
    <PackageItemSMS
      chosenPackage={chosenPackage}
      handleChoosePackageMobile={handleChoosePackageMobile}
      data={item}
      hasDiscountPackage={hasDiscountPackage}
      onDiscountEnd={handleDiscountEnd}
      isFirstTimePaid={isFirstTimePaid}
      discountForFirstPaidUser={discountForFirstPaidUser}
      currentUsingPackage={getUsingPackage(item?.type)}
      useNewPackageMobile={useNewPackageMobile}
    />
  );

  const renderPackages = (pkgs = []) => pkgs.map((item) => renderFlagUI(item));

  const checkUserFirstTimePaid = async () => {
    const payFees = 'pay_fees';
    const response = await api.orders.getUserPaidOrders(payFees);
    if (response.status && response.result?.orders.length < 1)
      if (discountForFirstPaidUser) setIsFirstTimePaid(true);
  };

  useEffect(() => {
    checkUserFirstTimePaid();
  }, [discountForFirstPaidUser]);

  useEffect(() => {
    fetchPackages();
  }, [packageType]);

  useEffect(() => {
    handleSetCurrentPackages(packages);
  }, [packages]);

  return (
    <StyledPackages
      isVietNam={language === LANGUAGE.VN}
      num={currentPackages.length}
      useNewPackageMobile={useNewPackageMobile}
      chosenPackage={chosenPackage}
    >
      {useNewPackageMobile ? (
        <div className="tabs-header">
          <NewPackageTypes
            types={newPackageTypeTabs}
            activeType={packageType}
            onChangeType={handleChangePackageType}
          />
        </div>
      ) : (
        <div className="tabs-header">
          <Typography className="package-type-label">
            {t('servicePricing')}
          </Typography>
          <PackageTypes
            isSMSUser
            types={packageTypeTabs}
            activeType={packageType}
            onChangeType={handleChangePackageType}
          />
        </div>
      )}

      <div ref={packagesRef} className="packages-wrapper">
        <ProcessHandler loading={loading} mt="30px" mb="30px" align="center">
          <div className="packages">
            {packagesWidth > PACKAGE_RESPONSIVE_BREAKPOINT
              ? renderPackages(currentPackages)
              : packagesWidth > PACKAGE_RESPONSIVE_MOBILE_BREAKPOINT
              ? renderPackages(currentPackages?.slice()?.sort(packageSort))
              : renderPackages(
                  currentPackages?.slice()?.sort(packageSortMobile),
                )}
          </div>
        </ProcessHandler>
      </div>

      <div className="notice-ai-voice-container">
        <span className="notice-ai-voice">{t('noticeAIVoice1')}</span>
        <span className="notice-ai-voice">{t('noticeAIVoice2')}</span>
      </div>
    </StyledPackages>
  );
};

export default SMSPackages;
